<template>
    <div class="border border-gray-200 rounded-lg overflow-hidden bg-white shadow-sm" data-testid="broadsheet-wizard-preview">
        <div class="h-2 bg-slate-700"></div>

        <div class="p-4">
            <h4 class="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1">Live Preview</h4>
            <p class="font-semibold text-gray-800 mb-3 truncate">
                {{ answers.name || 'Untitled Broadsheet' }}
            </p>

            <div v-if="infoColumns.length === 0 && answers.sourceKeys.length === 0"
                class="text-center py-10 border-2 border-dashed border-gray-200 rounded-lg text-gray-400 text-sm">
                <i class="pi pi-table text-2xl mb-2 block text-gray-300"></i>
                Your broadsheet builds up here as you answer.
            </div>

            <div v-else class="overflow-x-auto">
                <table class="w-full text-[11px] border border-gray-200">
                    <thead>
                        <!-- Subject group row -->
                        <tr class="bg-gray-100 text-gray-700">
                            <th v-for="col in infoColumns" :key="col.key" :rowspan="groupedScores ? 2 : 1"
                                class="px-2 py-1 text-left font-semibold border border-gray-200 whitespace-nowrap align-bottom">
                                {{ col.label }}
                            </th>
                            <th v-for="(subject, si) in subjectNames" :key="subject"
                                :colspan="Math.max(1, answers.sourceKeys.length)"
                                class="px-2 py-1 text-center font-semibold border border-gray-200 whitespace-nowrap">
                                <div class="leading-tight">
                                    <div>{{ subject }}</div>
                                    <div v-if="answers.showUnitInHeaders && subjectMeta[si]?.unit" class="text-[9px] font-normal text-gray-500">{{ subjectMeta[si].unit }}</div>
                                    <div v-if="answers.showStatusInHeaders && subjectMeta[si]?.status" class="text-[9px] font-normal text-gray-400">{{ subjectMeta[si].status }}</div>
                                </div>
                            </th>
                            <th v-if="answers.includePercentage" :rowspan="groupedScores ? 2 : 1"
                                class="px-2 py-1 text-center font-semibold border border-gray-200 align-bottom">%</th>
                            <th v-if="answers.positionChoice !== 'none'" :rowspan="groupedScores ? 2 : 1"
                                class="px-2 py-1 text-center font-semibold border border-gray-200 align-bottom">Pos</th>
                        </tr>
                        <!-- Score sub-columns when several source keys are grouped -->
                        <tr v-if="groupedScores" class="bg-gray-50 text-gray-500">
                            <template v-for="subject in subjectNames" :key="subject">
                                <th v-for="source in answers.sourceKeys" :key="`${subject}-${source.key}`"
                                    class="px-2 py-0.5 text-center font-medium border border-gray-200 whitespace-nowrap">
                                    {{ source.label }}
                                </th>
                            </template>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="row in rows" :key="String(row.info.student_name)" class="odd:bg-white even:bg-gray-50">
                            <td v-for="col in infoColumns" :key="col.key"
                                class="px-2 py-1 text-gray-700 border border-gray-100 whitespace-nowrap">
                                {{ row.info[col.key] }}
                            </td>
                            <template v-for="(subject, subjectIndex) in subjectNames" :key="subject">
                                <td v-for="(value, valueIndex) in row.subjects[subjectIndex]"
                                    :key="`${subject}-${valueIndex}`"
                                    class="px-2 py-1 text-center text-gray-600 border border-gray-100">
                                    {{ value }}
                                </td>
                            </template>
                            <td v-if="answers.includePercentage"
                                class="px-2 py-1 text-center text-gray-600 border border-gray-100">
                                {{ row.percentage }}
                            </td>
                            <td v-if="answers.positionChoice !== 'none'"
                                class="px-2 py-1 text-center font-semibold text-gray-700 border border-gray-100">
                                {{ ordinal(row.position) }}
                            </td>
                        </tr>
                    </tbody>
                </table>
                <p class="text-[10px] text-gray-400 mt-2">
                    Sample data — the real broadsheet expands to every
                    {{ subjectWord }} and student in the class.
                </p>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import {
  PREVIEW_COURSES,
  PREVIEW_SUBJECTS,
  STUDENT_INFO_OPTIONS,
  type BroadsheetPreviewRow,
  type BroadsheetWizardAnswers,
} from '@/utils/broadsheetWizard';

const props = defineProps<{
    answers: BroadsheetWizardAnswers;
    rows: BroadsheetPreviewRow[];
    isTertiary: boolean;
}>();

const subjectWord = computed(() => (props.isTertiary ? 'course' : 'subject'));

const PREVIEW_COURSE_META = [
  { code: 'CSC 101', name: 'Mathematics', unit: 3, status: 'C' },
  { code: 'MTH 102', name: 'English', unit: 2, status: 'C' },
  { code: 'GST 103', name: 'Basic Sci.', unit: 2, status: 'E' },
];

const subjectNames = computed(() => {
  if (props.answers.sourceKeys.length === 0) return [];
  if (props.isTertiary) {
    return props.answers.useCourseAbbreviation
      ? PREVIEW_COURSE_META.map((c) => c.code)
      : PREVIEW_COURSE_META.map((c) => c.name);
  }
  return PREVIEW_SUBJECTS;
});

const subjectMeta = computed(() => {
  if (!props.isTertiary) return PREVIEW_COURSE_META.map(() => ({ unit: null, status: null }));
  return PREVIEW_COURSE_META;
});

const infoColumns = computed(() => STUDENT_INFO_OPTIONS
  .filter((o) => props.answers.studentInfoKeys.includes(o.key) && (!o.tertiaryOnly || props.isTertiary))
  .map((o) => ({ ...o, label: props.isTertiary && o.tertiaryLabel ? o.tertiaryLabel : o.label })),
);

const groupedScores = computed(() => props.answers.sourceKeys.length > 1);

function ordinal(n: number): string {
  const suffix = ['th', 'st', 'nd', 'rd'][((n % 100) - 20) % 10] || ['th', 'st', 'nd', 'rd'][n % 100] || 'th';
  return `${n}${suffix}`;
}
</script>
